import Axios, { AxiosError, AxiosRequestConfig, AxiosInstance } from 'axios';
import CONFIG from '@/config';
import { getAuthToken } from './utils';

export const AXIOS_INSTANCE = Axios.create({
  timeout: 30000
});

export interface CustomRequestOptions extends AxiosRequestConfig {
  axiosInstance?: AxiosInstance;
}

export const customInstance = async <T>(
  config: AxiosRequestConfig & {
    service?: 'FINANCE' | 'BOOKING' | 'DOCUMENTS' | 'USERS';
  },
  options?: AxiosRequestConfig
): Promise<T> => {
  const token = getAuthToken();
  const instance = options?.axiosInstance || AXIOS_INSTANCE;

  const finalConfig: AxiosRequestConfig = {
    ...config,
    ...options,
    baseURL:
      config.service === 'FINANCE'
        ? CONFIG.FINANCE_API_URL
        : config.service === 'DOCUMENTS'
          ? CONFIG.DOCUMENTS_API_URL
          : config.service === 'USERS'
            ? CONFIG.USERS_API_URL
            : CONFIG.BOOKING_API_URL,
    headers: {
      ...(config.headers || {}),
      ...(options?.headers || {}),
      Authorization: `JWT ${token}`
    }
  };

  return instance(finalConfig)
    .then(response => response.data as T)
    .catch(error => {
      console.error('API request failed:', error);
      throw error;
    });
};

export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;

export async function bookingApiInstance<T>(
  config: Omit<AxiosRequestConfig, 'service'>,
  options?: CustomRequestOptions
): Promise<T> {
  return customInstance<T>({ ...config, service: 'BOOKING' }, options);
}

export async function financeApiInstance<T>(
  config: Omit<AxiosRequestConfig, 'service'>,
  options?: CustomRequestOptions
): Promise<T> {
  return customInstance<T>({ ...config, service: 'FINANCE' }, options);
}

export async function documentsApiInstance<T>(
  config: Omit<AxiosRequestConfig, 'service'>,
  options?: RequestInit
): Promise<T> {
  return customInstance<T>({ ...config, service: 'DOCUMENTS' }, options);
}

export async function usersApiInstance<T>(
  config: Omit<AxiosRequestConfig, 'service'>,
  options?: CustomRequestOptions
): Promise<T> {
  return customInstance<T>({ ...config, service: 'USERS' }, options);
}
